import { useEffect, useState } from 'preact/hooks';
import {
  ALL_FILTERS,
  ENTITY_ORDER,
  PERIODS,
  VIEWS,
  exceptionsByKind,
  exportCsv,
  filterOptions,
  headline,
  pipelineByStage,
  poByEntity,
  topBy,
  topSuppliers,
  type DashboardBody,
  type Filters,
  type ViewBy,
} from '../../shared/insights/dashboard';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, saveBytes } from '../lib/api';
import { formatDateTime } from '../lib/dates';

// Leadership dashboard (P5.2, screen 19; 01-SPEC §7, D67). One request per year: the nightly
// snapshot, already scoped by the broker (department heads see their own departments). Every
// filter is worked out here with shared/insights/dashboard.ts.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: DashboardBody };

const RIYADH_MONTH = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Riyadh',
  year: 'numeric',
  month: '2-digit',
});
export const thisMonth = () => RIYADH_MONTH.format(new Date()).slice(0, 7);
/** Short amounts as on screen 19: 6.84M, 312k, 940. */
export function compact(minor: number): string {
  const v = minor / 100;
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${Math.round(v / 1e3)}k`;
  return String(Math.round(v));
}
const ENTITY_NAME: Record<string, string> = { SMC: 'SMC Group', VIB: 'Vibes', MED: 'Media' };
const ENTITY_CLASS: Record<string, string> = { SMC: 'smc', VIB: 'vibes', MED: 'media' };

function Delta({ pct }: { pct: number | null }) {
  const { t } = useI18n();
  if (pct === null) return null;
  return (
    <span class={`caption dash-delta dash-delta--${pct >= 0 ? 'up' : 'down'}`} dir="ltr">
      {pct >= 0 ? '+' : ''}
      {pct}% {t('dash.vsLastYear')}
    </span>
  );
}

function Bars({ rows }: { rows: { label: string; value: number; text: string; sub?: string }[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul class="dash-bars">
      {rows.map((r) => (
        <li key={r.label}>
          <span class="dash-bars__label" dir="auto">
            {r.label}
            {r.sub && <span class="caption"> · {r.sub}</span>}
          </span>
          <span class="dash-bars__value" dir="ltr">
            {r.text}
          </span>
          <progress class="progress dash-bars__bar" max={max} value={r.value} aria-hidden="true" />
        </li>
      ))}
    </ul>
  );
}

/** Stacked bars of PO value per month and entity, drawn with SVG attributes (no inline style). */
function EntityChart({ data }: { data: ReturnType<typeof poByEntity> }) {
  const { t, lang } = useI18n();
  const W = 600;
  const H = 220;
  const top = 20;
  const bottom = 24;
  const max = Math.max(1, ...data.map((d) => d.total));
  const slot = W / Math.max(1, data.length);
  const bar = Math.min(64, slot * 0.7);
  const monthName = (m: string) =>
    new Intl.DateTimeFormat(lang === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB', {
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(`${m}-01T00:00:00Z`));
  return (
    <svg class="dash-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('dash.chartLabel')}>
      {data.map((d, i) => {
        const x = i * slot + (slot - bar) / 2;
        let y = H - bottom;
        return (
          <g key={d.month}>
            {ENTITY_ORDER.map((e) => {
              const v = d.values[e] ?? 0;
              if (!v) return null;
              const h = ((H - top - bottom) * v) / max;
              y -= h;
              return (
                <rect
                  key={e}
                  class={`dash-chart__${ENTITY_CLASS[e]}`}
                  x={x}
                  y={y}
                  width={bar}
                  height={h}
                >
                  <title>{`${ENTITY_NAME[e]} ${compact(v)}`}</title>
                </rect>
              );
            })}
            {d.total > 0 && (
              <text class="dash-chart__value" x={x + bar / 2} y={y - 4} text-anchor="middle">
                {compact(d.total)}
              </text>
            )}
            <text class="dash-chart__month" x={x + bar / 2} y={H - 6} text-anchor="middle">
              {monthName(d.month)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export interface HeadlineTile {
  key: string;
  label: MessageKey;
  value: string;
  sub: preact.ComponentChildren;
}

/** The six headline tiles of screen 19; Home shows the same tiles for this year (D78). */
export function headlineTiles(
  h: ReturnType<typeof headline>,
  t: (key: MessageKey, vars?: Record<string, string>) => string,
): HeadlineTile[] {
  return [
    {
      key: 'pr',
      label: 'dash.tile.pr',
      value: compact(h.prValue),
      sub: (
        <>
          <span class="caption">{t('dash.requests', { n: String(h.prCount) })}</span>{' '}
          <Delta pct={h.vsLastYear.prValue} />
        </>
      ),
    },
    {
      key: 'po',
      label: 'dash.tile.po',
      value: compact(h.poValue),
      sub: (
        <>
          <span class="caption">{t('dash.pos', { n: String(h.poCount) })}</span>{' '}
          <Delta pct={h.vsLastYear.poValue} />
        </>
      ),
    },
    {
      key: 'delivered',
      label: 'dash.tile.delivered',
      value: compact(h.delivered),
      sub: <span class="caption">{t('dash.ofPo', { n: String(h.deliveredPct) })}</span>,
    },
    {
      key: 'pipeline',
      label: 'dash.tile.pipeline',
      value: compact(h.pipelineValue),
      sub: <span class="caption">{t('dash.requests', { n: String(h.pipelineCount) })}</span>,
    },
    {
      key: 'days',
      label: 'dash.tile.prToPo',
      value: h.prToPoDays === null ? '—' : t('dash.days', { n: String(h.prToPoDays) }),
      sub: <span class="caption">{t('dash.prToPoHint')}</span>,
    },
    {
      key: 'pc',
      label: 'dash.tile.pettyCash',
      value: compact(h.pettyCash),
      sub: <Delta pct={h.vsLastYear.pettyCash} />,
    },
  ];
}

export function DashboardPage() {
  const { t, lang } = useI18n();
  const today = thisMonth();
  const thisYear = Number(today.slice(0, 4));
  const [year, setYear] = useState(thisYear);
  const [filters, setFilters] = useState<Filters>(ALL_FILTERS);
  const [view, setView] = useState<ViewBy>('department');
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    apiGet<DashboardBody>(`/api/dashboard?year=${year}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [year]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'forbidden' ? t('dash.forbidden') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }

  const data = load.data;
  const opts = filterOptions(data);
  const h = headline(data, filters, today);
  const set = (patch: Partial<Filters>) => setFilters({ ...filters, ...patch });
  const catName = (id: string) => {
    const c = data.categories.find((x) => x.id === id);
    return c ? (lang === 'ar' && c.nameAR ? c.nameAR : c.nameEN) : id;
  };
  const select = (
    id: string,
    label: MessageKey,
    value: string,
    values: readonly string[],
    onPick: (v: string) => void,
    all: MessageKey | null,
    show: (v: string) => string = (v) => v,
  ) => (
    <div class="field">
      <label for={id}>{t(label)}</label>
      <select id={id} value={value} onChange={(e) => onPick((e.target as HTMLSelectElement).value)}>
        {all && <option value="">{t(all)}</option>}
        {values.map((v) => (
          <option key={v} value={v}>
            {show(v)}
          </option>
        ))}
      </select>
    </div>
  );

  function download() {
    const csv = exportCsv(data, filters, today);
    saveBytes(new TextEncoder().encode(csv), `procure-dashboard-${data.year}.csv`, 'text/csv');
  }

  const tiles = headlineTiles(h, t);

  const stages = pipelineByStage(data, filters);
  const exceptions = exceptionsByKind(data, filters, today);
  const scopeText = [
    t(`dash.period.${filters.period}` as MessageKey),
    filters.entity ? (ENTITY_NAME[filters.entity] ?? filters.entity) : t('dash.allEntities'),
  ].join(' · ');

  return (
    <div class="stack">
      <div class="dash-head">
        <p class="caption">
          {scopeText} · {t('dash.amounts')}
        </p>
        <div class="queue-filters dash-filters">
          {select(
            'dash-year',
            'dash.year',
            String(year),
            [0, 1, 2, 3, 4].map((d) => String(thisYear - d)),
            (v) => setYear(Number(v)),
            null,
          )}
          {select(
            'dash-period',
            'dash.periodLabel',
            filters.period,
            PERIODS,
            (v) => set({ period: v as Filters['period'] }),
            null,
            (v) => t(`dash.period.${v}` as MessageKey),
          )}
          {select(
            'dash-entity',
            'dash.entity',
            filters.entity,
            opts.entities,
            (v) => set({ entity: v }),
            'dash.allEntities',
            (v) => ENTITY_NAME[v] ?? v,
          )}
          {select(
            'dash-vertical',
            'dash.vertical',
            filters.vertical,
            opts.verticals,
            (v) => set({ vertical: v }),
            'dash.allVerticals',
          )}
          {select(
            'dash-office',
            'dash.office',
            filters.office,
            opts.offices,
            (v) => set({ office: v }),
            'dash.allOffices',
          )}
          {select(
            'dash-department',
            'dash.department',
            filters.department,
            opts.departments,
            (v) => set({ department: v }),
            'dash.allDepartments',
          )}
        </div>
      </div>

      {!data.scope.all && (
        <p class="notice notice--info">
          {t('dash.scopeHead', { departments: data.scope.departments.join(', ') })}
        </p>
      )}
      {!data.current ? (
        <section class="card">
          <p>{t('dash.notBuilt', { year: String(data.year) })}</p>
        </section>
      ) : (
        <>
          <ul class="tiles dash-tiles" aria-label={t('dash.headline')}>
            {tiles.map((tile) => (
              <li key={tile.key} class="tile">
                <span class="caption">{t(tile.label)}</span>
                <span class="tile__value" dir="ltr">
                  {tile.value}
                </span>
                <span>{tile.sub}</span>
              </li>
            ))}
          </ul>
          {h.missingRates.length > 0 && (
            <p class="notice notice--action">
              {t('dash.missingRates', { currencies: h.missingRates.join(', ') })}
            </p>
          )}

          <div class="pv-layout dash-row">
            <section class="card stack" aria-labelledby="dash-chart">
              <div class="pv-row">
                <h2 id="dash-chart" class="card__title">
                  {t('dash.byEntity')}
                </h2>
                <ul class="dash-legend">
                  {ENTITY_ORDER.map((e) => (
                    <li key={e}>
                      <span
                        class={`dash-swatch dash-swatch--${ENTITY_CLASS[e]}`}
                        aria-hidden="true"
                      />
                      {ENTITY_NAME[e]}
                    </li>
                  ))}
                </ul>
              </div>
              <EntityChart data={poByEntity(data, filters, today)} />
            </section>
            <section class="card stack" aria-labelledby="dash-pipeline">
              <h2 id="dash-pipeline" class="card__title">
                {t('dash.pipeline')}
              </h2>
              <Bars
                rows={stages.map((s) => ({
                  label: t(`dash.stage.${s.stage}` as MessageKey),
                  value: s.count,
                  text: String(s.count),
                }))}
              />
              <p class="caption">
                {t('dash.avgDays')}{' '}
                {stages
                  .filter((s) => s.count > 0)
                  .map((s) => `${t(`dash.stage.${s.stage}` as MessageKey)} ${s.avgDays}`)
                  .join(' · ') || '—'}
              </p>
            </section>
          </div>

          <div class="dash-tops">
            <section class="card stack" aria-labelledby="dash-top-view">
              <div class="pv-row">
                <h2 id="dash-top-view" class="card__title">
                  {t(`dash.top.${view}` as MessageKey)}
                </h2>
                <div class="field dash-view">
                  <label class="visually-hidden" for="dash-view">
                    {t('dash.viewBy')}
                  </label>
                  <select
                    id="dash-view"
                    value={view}
                    onChange={(e) => setView((e.target as HTMLSelectElement).value as ViewBy)}
                  >
                    {VIEWS.map((v) => (
                      <option key={v} value={v}>
                        {t(`dash.view.${v}` as MessageKey)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <TopList rows={topBy(data, filters, today, view)} />
            </section>
            <section class="card stack" aria-labelledby="dash-top-cat">
              <h2 id="dash-top-cat" class="card__title">
                {t('dash.top.category')}
              </h2>
              <TopList
                rows={topBy(data, filters, today, 'category').map((r) => ({
                  ...r,
                  key: catName(r.key),
                }))}
              />
            </section>
            <section class="card stack" aria-labelledby="dash-top-sup">
              <h2 id="dash-top-sup" class="card__title">
                {t('dash.top.supplier')}
              </h2>
              {(() => {
                const rows = topSuppliers(data, filters, today);
                return rows.length === 0 ? (
                  <p class="caption">{t('dash.none')}</p>
                ) : (
                  <Bars
                    rows={rows.map((r) => ({
                      label: r.name || r.supplierId,
                      sub: t('dash.pos', { n: String(r.count) }),
                      value: r.value,
                      text: compact(r.value),
                    }))}
                  />
                );
              })()}
            </section>
          </div>

          <section class="card stack" aria-labelledby="dash-exceptions">
            <h2 id="dash-exceptions" class="card__title">
              {t('dash.exceptions')}
            </h2>
            <ul class="dash-exceptions">
              {exceptions.map((g) => (
                <li key={g.kind}>
                  <details>
                    <summary>
                      <span>{t(`dash.ex.${g.kind}` as MessageKey)}</span>
                      <span class={`pill ${g.items.length ? 'pill--warning' : 'pill--neutral'}`}>
                        {g.items.length}
                      </span>
                    </summary>
                    {g.items.length === 0 ? (
                      <p class="caption">{t('dash.none')}</p>
                    ) : (
                      <ul class="dash-exception-list">
                        {g.items.slice(0, 50).map((e) => (
                          <li key={`${e.prNo}-${e.docNo}`}>
                            <a href={`#/requests/${e.prNo}`} dir="ltr">
                              {e.prNo}
                            </a>
                            {e.docNo && (
                              <span class="caption" dir="ltr">
                                {' '}
                                · {e.docNo}
                              </span>
                            )}
                            <span class="caption">
                              {' '}
                              · {e.department} · {e.vertical}
                              {g.kind === 'rushFewQuotes' &&
                                ` · ${t('dash.quotesN', { n: String(e.n) })}`}
                              {g.kind === 'sentBeforeCfo' &&
                                ` · ${e.n < 0 ? t('dash.unsigned') : t('dash.days', { n: String(e.n) })}`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </details>
                </li>
              ))}
            </ul>
          </section>

          <div class="actions">
            <button type="button" class="button" onClick={download}>
              {t('dash.export')}
            </button>
            <p class="caption">
              {t('dash.builtAt', { at: formatDateTime(data.current.builtAt, lang) })}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function TopList({ rows }: { rows: { key: string; value: number }[] }) {
  const { t } = useI18n();
  if (rows.length === 0) return <p class="caption">{t('dash.none')}</p>;
  return (
    <Bars rows={rows.map((r) => ({ label: r.key, value: r.value, text: compact(r.value) }))} />
  );
}
